import { useMemo, useState } from "react";
import { Button, Modal, DataGrid, Flex } from "@repo/ping-design";
import { formatCurrency } from "../../../../utils/formatters";
import "./PermitsDetailModal.scss";

type PermitsDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data: any;
};

type PermitRow = {
  id: number;
  row_number: number;
  permit_id: string;
  permit_type: string;
  permit_class: string;
  status: string;
  issue_date: string;
  estimated_cost: string;
  description: string;
  permit: any;
};

const PERMIT_COLUMNS = [
  { key: "row_number", name: "#", width: 60 },
  { key: "status", name: "Status", resizable: true, width: 100 },
  {
    key: "estimated_cost",
    name: "Est. Cost",
    resizable: true,
    width: 120,
    renderCell: ({ row }: { row: PermitRow }) => {
      const val = row.estimated_cost;
      if (val == null || val === "") return null;
      const num = parseFloat(String(val));
      return isNaN(num) ? val : formatCurrency(num);
    },
  },
  { key: "permit_type", name: "Type", resizable: true, width: 120 },
  { key: "description", name: "Description", resizable: true },
  { key: "permit_id", name: "Permit ID", resizable: true, width: 140 },
  { key: "permit_class", name: "Class", resizable: true, width: 100 },
  { key: "issue_date", name: "Issue Date", resizable: true, width: 110 },
];

const formatDetailLabel = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDetailValue = (key: string, value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (key === "job_value" || key === "fees" || key === "estimated_cost") {
    return formatCurrency(Number(value));
  }
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return String(value);
};

const PermitDetailSection = ({
  title,
  values,
}: {
  title: string;
  values: Record<string, unknown>;
}) => (
  <section className="PermitsDetailModal__Permits__Expanded__Section">
    <h3>{title}</h3>
    <div className="PermitsDetailModal__Permits__Expanded__Fields">
      {Object.entries(values)
        .filter(([key]) => key !== "latitude" && key !== "longitude")
        .map(([key, value]) => (
        <div
          className="PermitsDetailModal__Permits__Expanded__Field"
          key={key}
        >
          <span className="PermitsDetailModal__Permits__Expanded__Field__Label">
            {formatDetailLabel(key)}
          </span>
          <span className="PermitsDetailModal__Permits__Expanded__Field__Value">
            {formatDetailValue(key, value)}
          </span>
        </div>
        ))}
    </div>
  </section>
);

const PermitsTab = ({ data }: { data: any }) => {
  const rows: PermitRow[] = useMemo(() => {
    const detailed = data?.permits_detailed;
    if (!Array.isArray(detailed)) return [];

    return detailed.map((permit: any, i: number) => {
      const summary = permit?.permit_summary || {};
      const details = permit?.permit_details || {};
      return {
        id: i,
        row_number: i + 1,
        permit_id: summary.permit_id || details.permit_id || "",
        permit_type: summary.permit_type || "",
        permit_class: summary.permit_class || "",
        status: summary.status || "",
        issue_date: summary.issue_date || "",
        estimated_cost: summary.estimated_cost || details.estimated_cost || "",
        description: details.description || "",
        permit,
      };
    });
  }, [data]);

  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const expandedRow = rows.find((row) => row.id === expandedRowId);

  return (
    <div className="PermitsDetailModal__Permits">
      {expandedRow && (
        <div className="PermitsDetailModal__Permits__Expanded">
          <div className="PermitsDetailModal__Permits__Expanded__Header">
            <Button
              label="Back"
              iconName="ArrowLeft"
              size="sm"
              variant="ghost"
              onClick={() => setExpandedRowId(null)}
            />
          </div>
          <PermitDetailSection
            title="Permit Summary"
            values={expandedRow.permit?.permit_summary || {}}
          />
          <PermitDetailSection
            title="Permit Details"
            values={expandedRow.permit?.permit_details || {}}
          />
        </div>
      )}
      {!expandedRow && (
        <DataGrid
          columns={PERMIT_COLUMNS}
          rows={rows}
          onCellClick={({ row }: { row: PermitRow }) =>
            setExpandedRowId((current) => (current === row.id ? null : row.id))
          }
        />
      )}
    </div>
  );
};

export const PermitsDetailModal = ({
  isOpen,
  onClose,
  data,
}: PermitsDetailModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Permits Detail"
      className="PermitsDetailModal"
      renderContent={() => (
        <Flex direction="column" gap="4" style={{ height: "100%" }}>
          <PermitsTab data={data} />
        </Flex>
      )}
    />
  );
};
